import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Grid,
  Paper,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { ClientInformation } from "../../components/client-booking/ClientInformation";
import { DeceasedInformation } from "../..//components/client-booking/DeceasedInformation";
import { ServiceInformation } from "../../components/client-booking/ServiceInfo";
import { PackageSelection } from "../../components/client-booking/PackageSelection";
import { PreviewPanel } from "../../components/client-booking/PreviewPanel";
import { ConferenceServiceInformation } from "../../components/client-booking/ConferenceServiceInfo";

import patternUrl from "../../../../../assets/pattern.png";
import { RootState } from "../../../../../store/Index";
import { BookingData } from "../../../../../types/bookings";
import { buildBookingFormData } from "../../../../bookings/utils/add-booking-form-data";
import { initialValues } from "../../../../bookings/AddBooking";
import { BookingResponse } from "../../../../bookings/types/add-booking";
import apiService from "../../../../../service/Api";

// ── Types ─────────────────────────────────────────────────────────────────────

export type BookingType = "FUNERAL" | "CONFERENCE";

// ── Stepper chrome ────────────────────────────────────────────────────────────

const CustomConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 15 },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: "rgba(255,255,255,0.6)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: { backgroundColor: "#fff" },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: "rgba(255,255,255,0.3)",
    borderRadius: 1,
  },
}));

const CustomStepIcon = styled("div")<{
  active?: boolean;
  completed?: boolean;
  browndark?: string;
}>(({ active, completed, browndark }) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: completed
    ? "#fff"
    : active
      ? "#fff"
      : "rgba(255,255,255,0.2)",
  color: completed
    ? (browndark ?? "#724E41")
    : active
      ? (browndark ?? "#724E41")
      : "rgba(255,255,255,0.6)",
  fontWeight: 700,
  fontSize: 14,
  boxShadow: active ? "0 0 0 3px rgba(255,255,255,0.4)" : "none",
  cursor: "pointer",
  transition: "all 0.2s ease",
}));

// ── Helper ────────────────────────────────────────────────────────────────────

function buildInitialData(): BookingData {
  return { ...initialValues };
}

// ── Component ─────────────────────────────────────────────────────────────────

const PublicBookingPage = () => {
  const { id: referralId } = useParams<{ id: string }>();
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const color10 = reduxTheme.primary_colors?.[10] ?? "#f5ede9";
  const color400 = reduxTheme.primary_colors?.[400] ?? "#724E41";
  const color700 = reduxTheme.primary_colors?.[700] ?? "#4a3028";
  const color800 = reduxTheme.primary_colors?.[800] ?? "#2a1f1c";
  const color900 = reduxTheme.primary_colors?.[900] ?? "#1a1210";

  // ── Phase: "picker" | "stepper" | "success" ───────────────────────────────
  const [phase, setPhase] = useState<"picker" | "stepper" | "success">(
    "picker",
  );
  const [bookingType, setBookingType] = useState<BookingType | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [bookingData, setBookingData] = useState<BookingData>(buildInitialData);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const steps =
    bookingType === "CONFERENCE"
      ? [
          "Client Information",
          "Conference Details",
          "Service Information",
          "Package Selection",
        ]
      : [
          "Client Information",
          "Deceased Information",
          "Service Information",
          "Package Selection",
        ];

  const isLastStep = activeStep === steps.length - 1;

  // Sync booking_type into form data whenever it changes
  useEffect(() => {
    if (bookingType) {
      setBookingData((prev) => ({ ...prev, booking_type: bookingType }));
    }
  }, [bookingType]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleTypeSelect = (type: BookingType) => {
    setBookingType(type);
    setActiveStep(0);
    setBookingData(buildInitialData());
    setSubmissionError(null);
    setPhase("stepper");
  };

  const handleBack = () => {
    if (activeStep === 0) {
      // Go back to type picker
      setPhase("picker");
      setBookingType(null);
    } else {
      setActiveStep((p) => Math.max(p - 1, 0));
    }
  };

  const handleNext = () => {
    if (isLastStep) {
      submitBooking(bookingData);
    } else {
      setActiveStep((p) => p + 1);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const submitBooking = useCallback(
    async (dataToSubmit: BookingData) => {
      setSubmitting(true);
      setSubmissionError(null);

      try {
        // Step 1: create contact
        const contactRes = await apiService.post<{ id: number }>(
          "/api/contacts/contact/",
          {
            first_name: dataToSubmit.first_name,
            last_name: dataToSubmit.surname,
            email: dataToSubmit.email,
            phone: dataToSubmit.phone,
            contact_type: "individual",
          },
        );

        // Step 2: enrich — referral_id only attached here (public client flow)
        const enrichedData: BookingData = {
          ...dataToSubmit,
          contact_id: contactRes.data.id,
          ...(referralId ? { referral_id: referralId } : {}),
        };

        // Step 3: submit booking
        const formData = buildBookingFormData(enrichedData);
        await apiService.post<BookingResponse>(
          "/api/bookings/booking/create-fullbooking/",
          formData,
        );

        toast.success("Booking created successfully!", {
          position: "top-right",
          autoClose: 2500,
          theme: "light",
        });

        setPhase("success");
      } catch (err: unknown) {
        let errorMessage = "Failed to create booking. Please try again.";
        const responseData = (
          err as {
            response?: { status?: number; data?: Record<string, unknown> };
          }
        )?.response;

        if (responseData?.status === 401) {
          errorMessage = "Session expired. Please refresh and try again.";
        } else if ((responseData?.data as { detail?: string })?.detail) {
          errorMessage =
            (responseData?.data as { detail?: string }).detail ?? errorMessage;
        } else if (responseData?.data) {
          if (typeof responseData.data === "string") {
            errorMessage = responseData.data;
          } else {
            try {
              errorMessage = Object.entries(responseData.data)
                .map(([key, value]) =>
                  Array.isArray(value)
                    ? `${key}: ${value.join(", ")}`
                    : `${key}: ${String(value)}`,
                )
                .join("; ");
            } catch {
              errorMessage = JSON.stringify(responseData.data);
            }
          }
        }

        setSubmissionError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [referralId],
  );

  // ── Step content ──────────────────────────────────────────────────────────

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <ClientInformation
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      case 1:
        return bookingType === "CONFERENCE" ? (
          <ConferenceServiceInformation
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        ) : (
          <DeceasedInformation
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      case 2:
        return (
          <ServiceInformation
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      case 3:
        return (
          <PackageSelection
            bookingData={bookingData}
            setBookingData={setBookingData}
            serviceType={bookingType ?? "FUNERAL"}
          />
        );
      default:
        return null;
    }
  };

  // ── Shared header background sx ───────────────────────────────────────────

  const headerSx = {
    p: 4,
    position: "relative",
    overflow: "hidden",
    height: 125,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    backgroundImage: `linear-gradient(135deg, ${color400}F5 0%, ${color10}F0 50%, ${color400}F0 100%), url(${patternUrl})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundBlendMode: "multiply",
    backdropFilter: "blur(10px)",
    borderRadius: "16px 16px 0 0",
  };

  // ── Phase: success ────────────────────────────────────────────────────────

  if (phase === "success") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: color900,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Fade in>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              p: 5,
              textAlign: "center",
              backgroundColor: color800,
              width: "100%",
              maxWidth: 380,
              boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "rgba(76,175,80,0.15)",
                border: "2px solid #4caf50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <Typography sx={{ fontSize: 28 }}>✓</Typography>
            </Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: "#fff", mb: 1 }}
            >
              Booking Received
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: color400, mb: 4, lineHeight: 1.6 }}
            >
              Thank you. We will be in touch shortly to confirm your
              arrangements.
            </Typography>
          </Paper>
        </Fade>
      </Box>
    );
  }

  // ── Phase: picker ─────────────────────────────────────────────────────────

  if (phase === "picker") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: color900,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Fade in>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: color900,
              boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
              width: "100%",
              maxWidth: 500,
            }}
          >
            {/* Header */}
            <Box sx={headerSx}>
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{
                  color: "#fff",
                  textTransform: "uppercase",
                  fontSize: "20px",
                  mb: 0.5,
                }}
              >
                Create New Booking
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#fff", fontWeight: 400, fontSize: "13px" }}
              >
                Select the category of the booking you wish to create.
              </Typography>
            </Box>

            {/* Cards */}
            <Box sx={{ p: 4 }}>
              <Grid container spacing={3}>
                {/* Funeral */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Button
                    onClick={() => handleTypeSelect("FUNERAL")}
                    sx={{
                      width: "100%",
                      minHeight: 180,
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textAlign: "center",
                      borderRadius: 4,
                      border: `1px solid ${color800}`,
                      backgroundColor: `${color800}40`,
                      textTransform: "none",
                      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        border: `1px solid ${color400}`,
                        backgroundColor: `${color400}12`,
                        transform: "translateY(-4px)",
                        boxShadow: `0 12px 24px rgba(0,0,0,0.3), 0 0 0 1px ${color400}`,
                        "& .type-picker-emoji": { transform: "scale(1.15)" },
                      },
                    }}
                  >
                    <Box
                      className="type-picker-emoji"
                      sx={{
                        fontSize: 42,
                        mb: 2,
                        lineHeight: 1,
                        transition: "transform 0.2s ease-in-out",
                      }}
                    >
                      ⚰️
                    </Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ color: color400, mb: 1, fontSize: "16px" }}
                    >
                      Funeral Services
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: color400,
                        fontSize: "12px",
                        lineHeight: 1.4,
                      }}
                    >
                      Set up a new funeral service arrangement.
                    </Typography>
                  </Button>
                </Grid>

                {/* Conference */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Button
                    onClick={() => handleTypeSelect("CONFERENCE")}
                    sx={{
                      width: "100%",
                      minHeight: 180,
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textAlign: "center",
                      borderRadius: 4,
                      border: `1px solid ${color800}`,
                      backgroundColor: `${color800}40`,
                      textTransform: "none",
                      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        border: `1px solid ${color400}`,
                        backgroundColor: `${color400}12`,
                        transform: "translateY(-4px)",
                        boxShadow: `0 12px 24px rgba(0,0,0,0.3), 0 0 0 1px ${color400}`,
                        "& .type-picker-emoji": { transform: "scale(1.15)" },
                      },
                    }}
                  >
                    <Box
                      className="type-picker-emoji"
                      sx={{
                        fontSize: 42,
                        mb: 2,
                        lineHeight: 1,
                        transition: "transform 0.2s ease-in-out",
                      }}
                    >
                      🏛️
                    </Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ color: color400, mb: 1, fontSize: "16px" }}
                    >
                      Conference / Event
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: color400,
                        fontSize: "12px",
                        lineHeight: 1.4,
                      }}
                    >
                      Set up a new conference or venue event.
                    </Typography>
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Fade>
      </Box>
    );
  }

  // ── Phase: stepper ────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: color900,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        p: { xs: 1, md: 3 },
        pt: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1200 }}>
        <Grid container spacing={3}>
          {/* Form */}
          <Grid size={{ xs: 12, md: 8 }} sx={{ display: "flex" }}>
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                borderRadius: 4,
                minHeight: "calc(100vh - 64px)",
                display: "flex",
                flexDirection: "column",
                backgroundColor: color900,
                boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  ...headerSx,
                  height: 120,
                  pt: 5,
                  pb: 2,
                  borderRadius: 0,
                }}
              >
                <Stepper
                  activeStep={activeStep}
                  alternativeLabel
                  connector={<CustomConnector />}
                >
                  {steps.map((label, index) => (
                    <Step key={label}>
                      <StepLabel
                        StepIconComponent={() => (
                          <CustomStepIcon
                            active={index === activeStep}
                            completed={index < activeStep}
                            browndark={color400}
                          >
                            {index + 1}
                          </CustomStepIcon>
                        )}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color:
                              index <= activeStep
                                ? "#fff"
                                : "rgba(255,255,255,0.5)",
                            fontWeight: index === activeStep ? 700 : 400,
                            letterSpacing: "0.02em",
                            display: "block",
                            mt: 0.5,
                          }}
                        >
                          {label}
                        </Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              {/* Body */}
              <Box
                sx={{
                  p: 4,
                  flex: 1,
                  overflowY: "auto",
                  backgroundColor: color900,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {renderStepContent(activeStep)}

                {submissionError && (
                  <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                    {submissionError}
                  </Typography>
                )}
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  p: 3,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 2,
                  borderTop: `1px solid ${color800}`,
                  backgroundColor: color900,
                }}
              >
                <Button
                  onClick={handleBack}
                  disabled={submitting}
                  sx={{
                    color: color400,
                    backgroundColor: "transparent",
                    border: `1px solid ${color400}`,
                    px: 3,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "13px",
                    "&:hover": { backgroundColor: `${color400}18` },
                    "&.Mui-disabled": {
                      borderColor: color800,
                      color: color700,
                      opacity: 0.5,
                    },
                  }}
                >
                  Back
                </Button>

                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={submitting}
                  sx={{
                    color: "#fff",
                    px: 4,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "13px",
                    background: `linear-gradient(135deg, ${color10} 0%, ${color400} 100%)`,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${color10} 0%, ${color400} 100%)`,
                    },
                    "&.Mui-disabled": { opacity: 0.4 },
                  }}
                >
                  {isLastStep ? (
                    submitting ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CircularProgress size={14} sx={{ color: "#fff" }} />
                        Submitting…
                      </Box>
                    ) : (
                      "Submit Booking"
                    )
                  ) : (
                    "Next"
                  )}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Preview */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box
              sx={{
                position: "sticky",
                top: 16,
                maxHeight: "calc(100vh - 64px)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <PreviewPanel formData={bookingData} />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default PublicBookingPage;
